import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CommissionTable = ({ transactions, onDisputeTransaction }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { color: 'bg-success text-success-foreground', label: 'Payé' },
      pending: { color: 'bg-warning text-warning-foreground', label: 'En attente' },
      processing: { color: 'bg-accent text-accent-foreground', label: 'En cours' },
      disputed: { color: 'bg-error text-error-foreground', label: 'Contesté' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const toggleRowExpansion = (transactionId) => {
    setExpandedRow(expandedRow === transactionId ? null : transactionId);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return `${amount?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="text-lg font-semibold text-card-foreground">Historique des commissions</h3>
      </div>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center space-x-1 hover:text-foreground"
                >
                  <span>Date</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Réservation
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('amount')}
                  className="flex items-center space-x-1 hover:text-foreground"
                >
                  <span>Montant</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {transactions?.map((transaction) => (
              <React.Fragment key={transaction?.id}>
                <tr className="hover:bg-muted/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-card-foreground">
                    {formatDate(transaction?.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-card-foreground">
                        {transaction?.venue}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {transaction?.artist} • {transaction?.eventType}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-card-foreground">
                      {formatAmount(transaction?.commission)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {transaction?.commissionRate}% de {formatAmount(transaction?.bookingValue)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(transaction?.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleRowExpansion(transaction?.id)}
                        iconName={expandedRow === transaction?.id ? "ChevronUp" : "ChevronDown"}
                      >
                        Détails
                      </Button>
                      {transaction?.status !== 'disputed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onDisputeTransaction(transaction?.id)}
                          iconName="AlertTriangle"
                        >
                          Contester
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedRow === transaction?.id && (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 bg-muted/30">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium text-card-foreground mb-2">Détails de la commission</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Valeur de réservation:</span>
                              <span className="text-card-foreground">{formatAmount(transaction?.bookingValue)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Taux de commission:</span>
                              <span className="text-card-foreground">{transaction?.commissionRate}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Modèle d'attribution:</span>
                              <span className="text-card-foreground">{transaction?.attributionModel}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Fenêtre d'attribution:</span>
                              <span className="text-card-foreground">{transaction?.attributionWindow}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-card-foreground mb-2">Informations de paiement</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Date de traitement:</span>
                              <span className="text-card-foreground">
                                {transaction?.processedDate ? formatDate(transaction?.processedDate) : 'En attente'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Méthode de paiement:</span>
                              <span className="text-card-foreground">{transaction?.paymentMethod}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">ID de transaction:</span>
                              <span className="text-card-foreground font-mono">{transaction?.transactionId}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden">
        {transactions?.map((transaction) => (
          <div key={transaction?.id} className="border-b border-border p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-card-foreground">
                {formatDate(transaction?.date)}
              </div>
              {getStatusBadge(transaction?.status)}
            </div>
            
            <div className="mb-3">
              <div className="text-base font-medium text-card-foreground mb-1">
                {transaction?.venue}
              </div>
              <div className="text-sm text-muted-foreground">
                {transaction?.artist} • {transaction?.eventType}
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-lg font-bold text-card-foreground">
                  {formatAmount(transaction?.commission)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {transaction?.commissionRate}% de {formatAmount(transaction?.bookingValue)}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleRowExpansion(transaction?.id)}
                iconName={expandedRow === transaction?.id ? "ChevronUp" : "ChevronDown"}
                fullWidth
              >
                {expandedRow === transaction?.id ? 'Masquer' : 'Voir détails'}
              </Button>
              {transaction?.status !== 'disputed' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDisputeTransaction(transaction?.id)}
                  iconName="AlertTriangle"
                >
                  Contester
                </Button>
              )}
            </div>

            {expandedRow === transaction?.id && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-card-foreground mb-2">Détails de la commission</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Modèle d'attribution:</span>
                        <span className="text-card-foreground">{transaction?.attributionModel}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Fenêtre d'attribution:</span>
                        <span className="text-card-foreground">{transaction?.attributionWindow}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-card-foreground mb-2">Informations de paiement</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date de traitement:</span>
                        <span className="text-card-foreground">
                          {transaction?.processedDate ? formatDate(transaction?.processedDate) : 'En attente'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Méthode:</span>
                        <span className="text-card-foreground">{transaction?.paymentMethod}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CommissionTable;